/** Auto-refresh mini-app after deploy (polls /api/app-version). */
import type { AppVersionResponse } from '../types/api.js';

interface AppVersionApiBody extends AppVersionResponse {
  ok?: boolean;
}

(function initVersionWatch() {
  if (window.location.protocol === 'file:') return;

  const STORAGE_KEY = 'ddpc_app_build';
  const POLL_MS = 30000;
  const FETCH_TIMEOUT_MS = 8000;
  const INFLIGHT_STALE_MS = 15000;
  const RELOAD_DELAY_MS = 1200;
  const FIRST_CHECK_DELAY_MS = 4000;

  let knownBuild: string | null = null;
  let reloadScheduled = false;
  let inFlight = false;
  let inFlightSince = 0;

  function readStoredBuild(): string {
    try {
      const v = sessionStorage.getItem(STORAGE_KEY);
      return v ? String(v).trim() : '';
    } catch (_) {
      return '';
    }
  }

  function storeBuild(build: string): void {
    try {
      sessionStorage.setItem(STORAGE_KEY, build);
    } catch (_) {}
  }

  function showUpdatingNotice(): void {
    try {
      const el = document.getElementById('app-notice');
      if (!el) return;
      el.textContent = 'Обновление приложения…';
      el.classList.add('app-notice-visible');
    } catch (_) {}
  }

  function scheduleReload(nextBuild: string): void {
    if (reloadScheduled) return;
    reloadScheduled = true;
    if (nextBuild) storeBuild(nextBuild);
    showUpdatingNotice();
    setTimeout(function () {
      try {
        window.location.reload();
      } catch (_) {
        const href = window.location.href.split('#')[0];
        const sep = href.indexOf('?') >= 0 ? '&' : '?';
        window.location.replace(href + sep + '_v=' + Date.now());
      }
    }, RELOAD_DELAY_MS);
  }

  function fetchBuildVersion(): Promise<string | null> {
    if (inFlight) {
      if (Date.now() - inFlightSince < INFLIGHT_STALE_MS) return Promise.resolve(null);
      inFlight = false;
    }
    inFlight = true;
    inFlightSince = Date.now();

    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const timeoutId = setTimeout(function () {
      try {
        if (controller) controller.abort();
      } catch (_) {}
    }, FETCH_TIMEOUT_MS);

    return fetch('/api/app-version?t=' + Date.now(), {
      cache: 'no-store',
      signal: controller ? controller.signal : undefined
    })
      .then(function (res) {
        return res.json().catch(function () {
          return null;
        });
      })
      .then(function (data: AppVersionApiBody | null) {
        if (!data || !data.ok || !data.version) return null;
        return String(data.version).trim();
      })
      .catch(function () {
        return null;
      })
      .finally(function () {
        clearTimeout(timeoutId);
        inFlight = false;
      });
  }

  function checkForNewBuild(): void {
    if (reloadScheduled) return;
    fetchBuildVersion().then(function (next) {
      if (!next) return;

      if (knownBuild == null) {
        const stored = readStoredBuild();
        knownBuild = stored || next;
        if (!stored) {
          storeBuild(next);
          return;
        }
        if (stored !== next) {
          scheduleReload(next);
          return;
        }
        return;
      }

      if (next !== knownBuild) scheduleReload(next);
    });
  }

  setTimeout(checkForNewBuild, FIRST_CHECK_DELAY_MS);
  setInterval(function () {
    if (document.visibilityState === 'visible') checkForNewBuild();
  }, POLL_MS);
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'visible') checkForNewBuild();
  });
})();
